import { FormBase } from "../../util/form-base-form";

export interface InfiniteScrollConfig {
    listSelector: string;
    itemSelector: string;
    paginationSelector?: string;
    nextLinkSelector?: string;
    rootMargin?: string;
    hidePaginationOnMobile?: boolean;
    appendExtras?: ((doc: Document, currentDoc: Document) => void) | null;
}

class InfiniteScrollFormBase extends FormBase {
    public getListElement(root: ParentNode, selector: string): HTMLElement | null {
        return this.queryElement(root, selector, HTMLElement);
    }

    public getPaginationElement(root: ParentNode, selector: string): HTMLElement | null {
        return this.queryElement(root, selector, HTMLElement);
    }

    public getSentinels(root: ParentNode): HTMLElement[] {
        return this.queryElements(root, ".infinite-scroll-sentinel", HTMLElement);
    }

    public getItems(root: ParentNode, selector: string): HTMLElement[] {
        return this.queryElements(root, selector, HTMLElement);
    }

    public getSpinner(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, ".infinite-scroll-sentinel__spinner", HTMLElement);
    }

    public getNextLink(root: ParentNode, selector: string): HTMLAnchorElement | null {
        return this.queryElement(root, selector, HTMLAnchorElement);
    }
}

export const infiniteScrollFormBase = new InfiniteScrollFormBase();

export class InfiniteScrollForm {
    public root: HTMLElement;
    public config: InfiniteScrollConfig;
    public listElement: Element | null;
    public paginationElement: Element | null;
    public sentinelElement: HTMLElement | null;
    public indicatorElement: HTMLElement | null;
    public observer: IntersectionObserver | null;
    public nextUrl: string | null;

    public constructor(root: HTMLElement, config: InfiniteScrollConfig) {
        this.root = root;
        this.config = config;

        this.listElement = infiniteScrollFormBase.getListElement(document, config.listSelector);
        this.paginationElement = infiniteScrollFormBase.getPaginationElement(
            document,
            config.paginationSelector || ".pagination-nav",
        );

        this.sentinelElement = null;
        this.indicatorElement = null;
        this.observer = null;
        this.nextUrl = null;
    }

    public getRoot(): HTMLElement {
        return this.root;
    }

    public getListElement(): HTMLElement | null {
        return this.listElement instanceof HTMLElement ? this.listElement : null;
    }

    public getListSelector(): string {
        return this.config.listSelector;
    }

    public getPaginationElement(): HTMLElement | null {
        return this.paginationElement instanceof HTMLElement ? this.paginationElement : null;
    }

    public getItemSelector(): string {
        return this.config.itemSelector;
    }

    public getPaginationSelector(): string {
        return this.config.paginationSelector || ".pagination-nav";
    }

    public getNextLinkSelector(): string {
        return this.config.nextLinkSelector || 'a[aria-label="Next page"]';
    }

    public getRootMargin(): string {
        return this.config.rootMargin || "220px 0px";
    }

    public shouldHidePaginationOnMobile(): boolean {
        return this.config.hidePaginationOnMobile !== false;
    }

    public getAppendExtras(): ((doc: Document, currentDoc: Document) => void) | null {
        return typeof this.config.appendExtras === "function" ? this.config.appendExtras : null;
    }

    public setSentinelElement(element: HTMLElement | null): void {
        this.sentinelElement = element;
    }

    public getSentinelElement(): HTMLElement | null {
        return this.sentinelElement instanceof HTMLElement ? this.sentinelElement : null;
    }

    public setIndicatorElement(element: HTMLElement | null): void {
        this.indicatorElement = element;
    }

    public getIndicatorElement(): HTMLElement | null {
        return this.indicatorElement instanceof HTMLElement ? this.indicatorElement : null;
    }

    public setObserver(observer: IntersectionObserver | null): void {
        this.observer = observer;
    }

    public getObserver(): IntersectionObserver | null {
        return this.observer instanceof IntersectionObserver ? this.observer : null;
    }

    public setNextUrl(url: string | null): void {
        this.nextUrl = url;
    }

    public getNextUrl(): string | null {
        return typeof this.nextUrl === "string" && this.nextUrl !== "" ? this.nextUrl : null;
    }

    public markInitialized(): void {
        this.root.dataset.infiniteScrollInitialized = "1";
    }
}
